import { useCallback, useEffect, useMemo, useRef } from "react";

import { isApolloError } from "@apollo/client";
import { useShallow } from "zustand/react/shallow";

import { RestockingSource } from "__graphql__/types";
import { RestockingProgressedMethod } from "analytics/events";
import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { useSerializePrivateRestockingList } from "flows/Inventory/flows/RestockingList/models/privateList/serializer";
import { RestockingItemStatus } from "flows/Inventory/flows/RestockingList/models/restockingItem/types";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import {
  useGetPublicRestockingListQuery,
  useGetPublicRestockingStatsLazyQuery,
} from "shared/queries/publicRestockingList/publicRestockingList.generated";
import { createRestockingItemComparator } from "utils/item";

import { usePrivateRestockingListQueries } from "../../hooks/usePrivateRestockingListQueries";
import { serializePublicRestockingList } from "../../models/publicList/serializer";
import { useSaveSkuToPublicRestockingListMutation } from "../../queries/publicRestockingList/publicRestockingList.generated";
import { useRestockingServiceStore } from "../../stores/restockingServiceStore";
import { applyRestockingFilters } from "../../utils/applyRestockingFilters";

const REMOVE_MULTIPLE_RESTOCKING_ITEMS_ERROR_TOAST_ID =
  "remove_multiple_restocking_items_error_toast";
const REMOVE_MULTIPLE_RESTOCKING_ITEMS_SUCCESS_TOAST_ID =
  "remove_multiple_restocking_items_success_toast";

export const usePublicRestockingItemCount = () => {
  const [fetchStats] = useGetPublicRestockingStatsLazyQuery({ fetchPolicy: "network-only" });
  const setAppBackgroundEffectsStore = useAppBackgroundEffectsStore(
    (state) => state.setAppBackgroundEffectsStore,
  );

  const refreshCount = useCallback(async () => {
    const { data } = await fetchStats();

    const count = data?.getPublicRestockingList?.publicRestockingList?.restockingItems?.length ?? 0;

    setAppBackgroundEffectsStore({ publicRestockingItemCount: count });
  }, [fetchStats, setAppBackgroundEffectsStore]);

  return { refreshPublicRestockingItemCount: refreshCount };
};

export const usePublicRestockingList = () => {
  const {
    data: publicRestockingListData,
    loading: isLoadingPublicRestockingList,
    refetch,
  } = useGetPublicRestockingListQuery();

  const [addItemToPublicRestockingListMutation] = useSaveSkuToPublicRestockingListMutation();

  const setAppBackgroundEffectsStore = useAppBackgroundEffectsStore(
    (state) => state.setAppBackgroundEffectsStore,
  );

  useEffect(() => {
    const count =
      publicRestockingListData?.getPublicRestockingList?.publicRestockingList?.restockingItems
        ?.length ?? 0;
    setAppBackgroundEffectsStore({ publicRestockingItemCount: count });
  }, [publicRestockingListData, setAppBackgroundEffectsStore]);

  const restockingItems = useMemo(() => {
    return publicRestockingListData
      ? serializePublicRestockingList(publicRestockingListData).restockingItems
      : null;
  }, [publicRestockingListData]);

  const addItemToPublicRestockingList = useCallback(
    async (sku: string, source: RestockingSource = RestockingSource.manual) => {
      await addItemToPublicRestockingListMutation({
        variables: { input: { sku, source } },
      });
      await refetch();
    },
    [addItemToPublicRestockingListMutation, refetch],
  );

  return {
    restockingItems,
    isLoadingPublicRestockingList,
    addItemToPublicRestockingList,
  };
};

export const usePrivateRestockingList = (
  { loadOnInit }: { loadOnInit: boolean } = { loadOnInit: true },
) => {
  const { refreshPublicRestockingItemCount } = usePublicRestockingItemCount();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { showToastUI } = useCustomToast();

  const {
    getPrivateRestockingListQuery,
    deletePrivateRestockingListMutation,
    restockItemMutation,
    createPrivateRestockingListMutation,
    removeMultipleSkusFromRestockingPrivateList,
    deleteSkuFromPrivateList,
  } = usePrivateRestockingListQueries();

  const {
    privateRestockingListData,
    sortOrder,
    sortBy,
    isLoadingPrivateRestockingList,
    itemInRestockingProcess,
    totalRestockingItemCount,
    privateRestockingListRefreshNeeded,
    categoryFilters,
    shelfFilters,
    priorityFilters,
    setPrivateRestockingListData,
    toggleSortOrder,
    updateSelectedItemsForRemoval,
    updateCategoryFilters,
    updateShelfFilters,
    updatePriorityFilters,
    setIsLoadingPrivateRestockingList,
    setItemInRestockingProcess,
    getCurrentPrivateRestockingListId,
    getShelfNumber,
    setPrivateRestockingListRefreshNeeded,
  } = useRestockingServiceStore(useShallow((state) => state));

  const serializePrivateRestockingList = useSerializePrivateRestockingList();
  const isLoadingPrivateRestockingListRef = useRef(false);

  const setIsLoading = useCallback(
    (v: boolean) => {
      // Using both a ref AND state here. The ref is so that we don't
      // run two requests simultaneously, the state is so that components
      // can subscribe to changes.
      isLoadingPrivateRestockingListRef.current = v;
      setIsLoadingPrivateRestockingList(v);
    },
    [setIsLoadingPrivateRestockingList],
  );

  const allRestockingItems = useMemo(
    () => privateRestockingListData?.restockingItems ?? [],
    [privateRestockingListData?.restockingItems],
  );

  const notDoneRestockingItems = useMemo(
    () => allRestockingItems.filter((item) => item.status !== RestockingItemStatus.done),
    [allRestockingItems],
  );

  const compareItems = useMemo(
    () => createRestockingItemComparator(sortBy, sortOrder),
    [sortBy, sortOrder],
  );

  const filteredTranslatedRestockingItems = useMemo(() => {
    const filtered = applyRestockingFilters(
      notDoneRestockingItems,
      categoryFilters,
      shelfFilters,
      priorityFilters,
    );
    return [...filtered].sort(compareItems);
  }, [notDoneRestockingItems, categoryFilters, shelfFilters, priorityFilters, compareItems]);

  const filteredPreparationItems = useMemo(() => {
    const filtered = applyRestockingFilters(
      allRestockingItems,
      categoryFilters,
      shelfFilters,
      priorityFilters,
    );
    return [...filtered].sort(compareItems);
  }, [allRestockingItems, categoryFilters, shelfFilters, priorityFilters, compareItems]);

  const sortedTranslatedRestockingItems = useMemo(() => {
    if (!privateRestockingListData?.restockingItems?.length) return [];
    return [...privateRestockingListData.restockingItems].sort(compareItems);
  }, [privateRestockingListData?.restockingItems, compareItems]);

  const loadPrivateRestockingList = useCallback(async () => {
    if (isLoadingPrivateRestockingListRef.current) return null;
    setIsLoading(true);
    try {
      const { data } = await getPrivateRestockingListQuery();
      if (data) {
        const serializedResponse = serializePrivateRestockingList(data);
        setPrivateRestockingListData(serializedResponse);
        return serializedResponse;
      } else {
        setPrivateRestockingListData(null);
        return null;
      }
    } catch (error) {
      if (!isApolloError(error as Error)) {
        throw error;
      }
      setPrivateRestockingListData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [
    getPrivateRestockingListQuery,
    serializePrivateRestockingList,
    setIsLoading,
    setPrivateRestockingListData,
  ]);

  const restockItem = useCallback(
    async (sku: string) => {
      if (!privateRestockingListData?.id || itemInRestockingProcess !== null) return;
      setItemInRestockingProcess(sku);
      try {
        const { data } = await restockItemMutation(privateRestockingListData.id, sku);
        if (data?.restockItem?.message === "The restocking item has been successfully updated.") {
          sendSegmentTrackEvent("restockingProgressed", {
            restocking_list_id: privateRestockingListData.id,
            action: "product_restocked",
            shelf_number: getShelfNumber(sku),
            product_sku: sku,
          });

          const updatedItems = privateRestockingListData.restockingItems.map((item) =>
            item.sku === sku ? { ...item, status: RestockingItemStatus.done } : item,
          );
          setPrivateRestockingListData({
            ...privateRestockingListData,
            restockingItems: updatedItems,
          });
        }
      } catch (error) {
        if (!isApolloError(error as Error)) {
          setItemInRestockingProcess(null);
          throw error;
        }
        await loadPrivateRestockingList();
      } finally {
        setItemInRestockingProcess(null);
      }
    },
    [
      getShelfNumber,
      itemInRestockingProcess,
      loadPrivateRestockingList,
      privateRestockingListData,
      restockItemMutation,
      sendSegmentTrackEvent,
      setItemInRestockingProcess,
      setPrivateRestockingListData,
    ],
  );

  const deletePrivateList = useCallback(async () => {
    if (!privateRestockingListData?.id) return;
    try {
      await deletePrivateRestockingListMutation(privateRestockingListData.id);
      sendSegmentTrackEvent("restockingStateUpdated", {
        restocking_list_id: privateRestockingListData.id,
        state: "restocking_list_finished",
      });
    } catch (error) {
      if (!isApolloError(error as Error)) {
        throw error;
      }
    } finally {
      await loadPrivateRestockingList();
    }
  }, [
    deletePrivateRestockingListMutation,
    loadPrivateRestockingList,
    privateRestockingListData?.id,
    sendSegmentTrackEvent,
  ]);

  const addItemsToPrivateRestockingList = useCallback(
    async (skus: string[], method: RestockingProgressedMethod) => {
      try {
        await createPrivateRestockingListMutation(skus);
      } catch (error) {
        if (isApolloError(error as Error)) {
          await refreshPublicRestockingItemCount();
        }
        throw error;
      }
      await refreshPublicRestockingItemCount();
      await loadPrivateRestockingList();

      const currentPrivateRestockingListId = getCurrentPrivateRestockingListId();
      if (currentPrivateRestockingListId) {
        if (!totalRestockingItemCount) {
          sendSegmentTrackEvent("restockingStateUpdated", {
            restocking_list_id: currentPrivateRestockingListId,
            state: "restocking_list_preparation_started",
          });
        }
        skus.forEach((sku: string) => {
          sendSegmentTrackEvent("restockingProgressed", {
            restocking_list_id: currentPrivateRestockingListId,
            action: "product_added_to_list",
            method,
            shelf_number: getShelfNumber(sku),
            product_sku: sku,
          });
        });
      }

      if (categoryFilters.length > 0 || shelfFilters.length > 0 || priorityFilters.length > 0) {
        updateCategoryFilters([]);
        updateShelfFilters([]);
        updatePriorityFilters([]);
      }
    },
    [
      createPrivateRestockingListMutation,
      getCurrentPrivateRestockingListId,
      getShelfNumber,
      loadPrivateRestockingList,
      refreshPublicRestockingItemCount,
      sendSegmentTrackEvent,
      totalRestockingItemCount,
      categoryFilters,
      shelfFilters,
      priorityFilters,
      updateCategoryFilters,
      updateShelfFilters,
      updatePriorityFilters,
    ],
  );

  const removeRestockingItem = useCallback(
    async (sku: string) => {
      if (!privateRestockingListData?.id) return;
      try {
        const shelfNumber = getShelfNumber(sku);
        await deleteSkuFromPrivateList(privateRestockingListData.id, sku);
        await refreshPublicRestockingItemCount();
        await loadPrivateRestockingList();
        sendSegmentTrackEvent("restockingProgressed", {
          restocking_list_id: privateRestockingListData.id,
          action: "product_removed_from_list",
          shelf_number: shelfNumber,
          product_sku: sku,
        });
      } catch (error) {
        if (!isApolloError(error as Error)) {
          throw error;
        }
      }
    },
    [
      deleteSkuFromPrivateList,
      getShelfNumber,
      loadPrivateRestockingList,
      privateRestockingListData?.id,
      refreshPublicRestockingItemCount,
      sendSegmentTrackEvent,
    ],
  );

  const removeMultipleRestockingItems = useCallback(
    async (skus: string[]) => {
      const listId = privateRestockingListData?.id;
      if (!listId) return;

      try {
        await removeMultipleSkusFromRestockingPrivateList(listId, skus);
        updateSelectedItemsForRemoval([]);
        await refreshPublicRestockingItemCount();
        await loadPrivateRestockingList();
        skus.forEach((sku: string) => {
          sendSegmentTrackEvent("restockingProgressed", {
            restocking_list_id: privateRestockingListData.id,
            action: "product_removed_from_list",
            shelf_number: getShelfNumber(sku),
            product_sku: sku,
          });
        });
        showToastUI({
          id: REMOVE_MULTIPLE_RESTOCKING_ITEMS_SUCCESS_TOAST_ID,
          status: "success",
          title: "flows.restocking-list.services.remove-multiple-restocking-items-success_toast",
        });
      } catch (error: any) {
        const isRestockingListNotFound =
          isApolloError(error) &&
          error.graphQLErrors?.[0]?.extensions?.code === "RESTOCKING_PRIVATE_LIST_NOT_FOUND";
        const toastTitle = isRestockingListNotFound
          ? "flows.restocking-list.services.remove-multiple-restocking-items-error-toast.not-found"
          : "flows.restocking-list.services.remove-multiple-restocking-items-error-toast";
        showToastUI({
          id: REMOVE_MULTIPLE_RESTOCKING_ITEMS_ERROR_TOAST_ID,
          title: toastTitle,
        });
        throw error;
      }
    },
    [
      getShelfNumber,
      loadPrivateRestockingList,
      privateRestockingListData?.id,
      refreshPublicRestockingItemCount,
      removeMultipleSkusFromRestockingPrivateList,
      sendSegmentTrackEvent,
      showToastUI,
      updateSelectedItemsForRemoval,
    ],
  );

  const refreshPrivateRestockingList = useCallback(async () => {
    await loadPrivateRestockingList();
  }, [loadPrivateRestockingList]);

  useEffect(() => {
    if (loadOnInit && privateRestockingListData === null) {
      loadPrivateRestockingList();
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (privateRestockingListRefreshNeeded) {
      refreshPrivateRestockingList().finally(() => {
        setPrivateRestockingListRefreshNeeded(false);
      });
    }
  }, [
    privateRestockingListRefreshNeeded,
    refreshPrivateRestockingList,
    setPrivateRestockingListRefreshNeeded,
  ]);

  return {
    privateRestockingListData,
    notDoneRestockingItems,
    filteredTranslatedRestockingItems,
    filteredPreparationItems,
    sortedTranslatedRestockingItems,
    sortOrder,
    isLoadingPrivateRestockingList,
    itemInRestockingProcess,
    restockItem,
    toggleSortOrder,
    deletePrivateList,
    addItemsToPrivateRestockingList,
    removeRestockingItem,
    removeMultipleRestockingItems,
    deleteSkuFromPrivateList,
  };
};
